export interface User {
  id: 'user1' | 'user2';
  name: string;
  avatar: string;
}

export interface AuthState {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
}

class AuthService {
  private currentUser: User | null = null;
  private token: string | null = null;
  private listeners: ((authState: AuthState) => void)[] = [];
  private isLoading = false;
  private apiUrl = process.env.REACT_APP_API_URL || '';

  constructor() {
    this.loadAuthState();
  }

  private loadAuthState() {
    const savedAuth = localStorage.getItem('loveMazeAuth');
    if (savedAuth) {
      try {
        const { user, token, timestamp } = JSON.parse(savedAuth);
        
        // Optional: check if token is expired based on timestamp
        const tokenMaxAge = 24 * 60 * 60 * 1000; // 24 hours
        if (new Date().getTime() - new Date(timestamp).getTime() > tokenMaxAge) {
          this.clearAuthState();
          return;
        }

        if (user && token) {
          this.currentUser = user;
          this.token = token;
          this.notifyListeners();
        }
      } catch (error) {
        console.error('Error loading auth state:', error);
        this.clearAuthState();
      }
    }
  }

  private saveAuthState(token: string, user: User) {
    this.token = token;
    this.currentUser = user;
    localStorage.setItem('loveMazeAuth', JSON.stringify({
      token,
      user,
      timestamp: new Date().toISOString()
    }));
  }

  private clearAuthState() {
    this.currentUser = null;
    this.token = null;
    localStorage.removeItem('loveMazeAuth');
  }

  public async login(passkey: string): Promise<boolean> {
    this.isLoading = true;
    this.notifyListeners();

    try {
      const response = await fetch(`${this.apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passkey }),
      });

      const data = await response.json();

      if (data.success && data.token && data.user) {
        this.saveAuthState(data.token, data.user);
        this.notifyListeners();
        return true;
      } else {
        throw new Error(data.error || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      this.clearAuthState();
      this.notifyListeners();
      return false;
    } finally {
      this.isLoading = false;
      this.notifyListeners();
    }
  }

  public logout() {
    this.clearAuthState();
    this.notifyListeners();
  }

  public getCurrentUser(): User | null {
    return this.currentUser;
  }

  public getToken(): string | null {
    return this.token;
  }

  public isAuthenticated(): boolean {
    return !!this.token && !!this.currentUser;
  }

  public getAuthState(): AuthState {
    return {
      currentUser: this.currentUser,
      isAuthenticated: this.isAuthenticated(),
      isLoading: this.isLoading,
      token: this.token,
    };
  }

  public onAuthStateChange(listener: (authState: AuthState) => void) {
    this.listeners.push(listener);
    // Immediately notify the new listener with the current state
    listener(this.getAuthState());
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    const authState = this.getAuthState();
    this.listeners.forEach(listener => listener(authState));
  }
}

// Create singleton instance
export const authService = new AuthService();
